import React, { useState, useRef, useCallback } from "react";
import * as XLSX from "xlsx";

// ─────────────────────────────────────────────────────────────
// DataUploadCenter.js
// Allows admins to upload real school data files.
// Supports: iReady (ELA + Math), Acadience (Reading + Math),
//           Student Roster (ROCL), Teacher Observations
// ─────────────────────────────────────────────────────────────

const T = {
  bg:"#EEF3FA", white:"#FFFFFF", navy:"#0A1628", ink:"#152744",
  blue:"#1560BD", sky:"#2E8BC0", teal:"#00A878", mint:"#D4F5EC",
  amber:"#F59E0B", rose:"#E03B3B", blush:"#FFF0F0", purple:"#7C3AED",
  slate:"#5A7293", mist:"#E2EAF4", light:"#EBF2FC", orange:"#EA580C",
  green:"#059669",
};

// ── DATA SOURCE REGISTRY ──────────────────────────────────────
// Each source knows how to detect and parse its file format
const DATA_SOURCES = [
  {
    id: "roster",
    label: "Student Roster",
    icon: "👥",
    color: T.blue,
    description: "Upload your ROCL spreadsheet with student names, codes, teachers, and class assignments.",
    accepts: ".xlsx,.xls,.csv",
    sheets: ["ROCL","1st ROCL","2nd ROCL","3rd ROCL","4th ROCL","5th ROCL","Kinder ROCL"],
    requiredCols: ["Student Code","Teacher"],
    format: `Sheet name: ROCL (or grade-specific ROCL sheets)\nRequired columns: Name, Student Code, Student ID, CLASS, Teacher 1, Teacher`,
    detect: (sheetNames) => sheetNames.some(s => s.includes("ROCL")),
    parse: parseRoster,
  },
  {
    id: "iready_ela",
    label: "iReady ELA",
    icon: "📖",
    color: T.blue,
    description: "Upload your iReady ELA diagnostic export. Supports per-grade sheets (K ELA iReady, 1st ELA iReady, etc.) or the combined Sheet1.",
    accepts: ".xlsx,.xls,.csv",
    sheets: ["Sheet1","K ELA iReady","1st ELA iReady","2nd ELA iReady","3rd ELA iReady","4th ELA iReady","5th ELA iReady"],
    requiredCols: ["Student","Scale Score","Overall Placement"],
    format: `Sheet names: "[Grade] ELA iReady" (e.g. "3rd ELA iReady") or "Sheet1"\nKey columns: Student, Scale Score, Overall Placement, Annual Typical Growth Measure, Lexile Measure, Percentile Rank`,
    detect: (sheetNames) => sheetNames.some(s => s.includes("ELA iReady") || s.includes("ELA iready")),
    parse: parseIReadyELA,
  },
  {
    id: "iready_math",
    label: "iReady Math",
    icon: "🔢",
    color: T.teal,
    description: "Upload your iReady Math diagnostic export. Supports per-grade sheets (K Math iReady, 1st Math iReady, etc.).",
    accepts: ".xlsx,.xls,.csv",
    sheets: ["K Math iReady","1st Math iReady","2nd Math iReady","3rd Math iReady","4th Math iReady","5th Math iready"],
    requiredCols: ["Student","Scale Score","Overall Placement"],
    format: `Sheet names: "[Grade] Math iReady" (e.g. "3rd Math iReady")\nKey columns: Student, Scale Score, Overall Placement, Annual Typical Growth Measure, Quantile Measure, Percentile Rank`,
    detect: (sheetNames) => sheetNames.some(s => s.includes("Math iReady") || s.includes("Math iready")),
    parse: parseIReadyMath,
  },
  {
    id: "acadience_reading",
    label: "Acadience Reading",
    icon: "📚",
    color: T.purple,
    description: "Upload your Acadience Reading export with BOY and MOY benchmark data.",
    accepts: ".xlsx,.xls,.csv",
    sheets: ["Acadience BOY - Reading","Acadience MOY - Reading"],
    requiredCols: ["Student Code","Student Grade Level","Benchmark Period"],
    format: `Sheet names: "Acadience BOY - Reading" and/or "Acadience MOY - Reading"\nKey columns: Student Code, Student Grade Level, Benchmark Period, ORF WC Score, ORF WC Status, Reading Composite Score`,
    detect: (sheetNames) => sheetNames.some(s => s.includes("Acadience") && s.includes("Reading")),
    parse: parseAcadienceReading,
  },
  {
    id: "acadience_math",
    label: "Acadience Math",
    icon: "🧮",
    color: T.orange,
    description: "Upload your Acadience Math export with BOY and MOY benchmark data.",
    accepts: ".xlsx,.xls,.csv",
    sheets: ["Acadience BOY- Math","Acadience MOY- Math"],
    requiredCols: ["Student","Student Grade Level","Benchmark Period"],
    format: `Sheet names: "Acadience BOY- Math" and/or "Acadience MOY- Math"\nKey columns: Student, Student Grade Level, Benchmark Period, Math Composite Score, Math Composite Status`,
    detect: (sheetNames) => sheetNames.some(s => s.includes("Acadience") && s.includes("Math")),
    parse: parseAcadienceMath,
  },
  {
    id: "observations",
    label: "Teacher Observations",
    icon: "👁️",
    color: T.amber,
    description: "Upload your Advance/MOTP observation report. Works with the standard NYC DOE observation export format.",
    accepts: ".xlsx,.xls,.csv",
    sheets: ["PageNot2014_1"],
    requiredCols: ["TEACHER","Observation Date"],
    format: `Sheet name: PageNot2014_1 (Advance MOTP Detail report)\nKey columns: TEACHER, DBN, Observation Date, Observer, Grade Level, Subject, Status, Domain scores`,
    detect: (sheetNames) => sheetNames.some(s => s.includes("PageNot") || s.includes("MOTP")),
    parse: parseObservations,
  },
];

// ── PARSERS ───────────────────────────────────────────────────

function parseRoster(workbook) {
  const rosterSheets = workbook.SheetNames.filter(s => s.includes("ROCL"));
  const students = [];

  for (const sheetName of rosterSheets) {
    const ws = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(ws, { defval: null });
    for (const row of rows) {
      if (!row["Student Code"]) continue;
      students.push({
        code:      row["Student Code"],
        studentId: row["Student ID"],
        atsCode:   row["ATS Code"],
        classId:   row["CLASS"],
        teacherAbbr: row["Teacher 1"],
        teacherFull: row["Teacher"],
        lastName:  row["Name"],
        grade:     sheetName.replace(" ROCL","").replace("Kinder","K"),
      });
    }
  }
  return { type:"roster", count: students.length, data: students };
}

function parseIReadyELA(workbook) {
  const elaSheets = workbook.SheetNames.filter(s =>
    s.includes("ELA iReady") || s.includes("ELA iready") || s === "Sheet1"
  );
  const students = [];

  for (const sheetName of elaSheets) {
    const ws = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(ws, { defval: null });
    const gradeFromSheet = sheetName.split(" ")[0]; // "3rd", "K", etc.

    for (const row of rows) {
      const code = row["Student"] || row["Student Code"];
      if (!code) continue;
      students.push({
        code,
        grade:      gradeFromSheet !== "Sheet1" ? gradeFromSheet : null,
        elaScore:   row["Scale Score"],
        elaLevel:   row["Overall Placement"],
        elaRelative: row["5-Level Overall Relative Placement"],
        elaGrowthTypical: row["Annual Typical Growth Measure"],
        elaGrowthStretch: row["Annual Stretch Growth Measure"],
        lexile:     row["Lexile Measure"],
        percentile: row["Percentile Rank"],
        date:       row["Date"],
        phonologicalAwareness: row["Phonological Awareness Placement"],
        phonics:    row["Phonics Placement"],
        vocabulary: row["Vocabulary Placement"],
        comprehension: row["Comprehension: Overall Placement"],
        writing:    row["Writing Placement"] || null,
        projNoGrowth: row["Proficiency if Student Shows No Additional Growth"],
        projTypical:  row["Projection if Student Achieves Typical Growth"],
        projStretch:  row["Projection if Student Achieves Stretch Growth"],
      });
    }
  }
  return { type:"iready_ela", count: students.length, data: students };
}

function parseIReadyMath(workbook) {
  const mathSheets = workbook.SheetNames.filter(s =>
    s.includes("Math iReady") || s.includes("Math iready")
  );
  const students = [];

  for (const sheetName of mathSheets) {
    const ws = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(ws, { defval: null });
    const gradeFromSheet = sheetName.split(" ")[0];

    for (const row of rows) {
      const code = row["Student"] || row["Student Code"];
      if (!code) continue;
      students.push({
        code,
        grade:      gradeFromSheet,
        mathScore:  row["Scale Score"],
        mathLevel:  row["Overall Placement"],
        mathRelative: row["5-Level Overall Relative Placement"],
        mathGrowthTypical: row["Annual Typical Growth Measure"],
        mathGrowthStretch: row["Annual Stretch Growth Measure"],
        quantile:   row["Quantile Measure"],
        percentile: row["Percentile Rank"],
        date:       row["Date"],
        numberOps:  row["Number and Operations Placement"],
        algebra:    row["Algebra and Algebraic Thinking Placement"],
        measurement: row["Measurement and Data Placement"],
        geometry:   row["Geometry Placement"],
        projNoGrowth: row["Proficiency if Student Shows No Additional Growth"],
        projTypical:  row["Projection if Student Achieves Typical Growth"],
        projStretch:  row["Projection if Student Achieves Stretch Growth"],
      });
    }
  }
  return { type:"iready_math", count: students.length, data: students };
}

function parseAcadienceReading(workbook) {
  const periods = ["BOY","MOY","EOY"];
  const records = [];

  for (const period of periods) {
    const sheetName = workbook.SheetNames.find(s =>
      s.includes("Acadience") && s.includes("Reading") &&
      s.toUpperCase().includes(period)
    );
    if (!sheetName) continue;
    const ws = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(ws, { defval: null });

    for (const row of rows) {
      if (!row["Student Code"]) continue;
      records.push({
        code:          row["Student Code"],
        grade:         row["Student Grade Level"],
        period:        period,
        orfScore:      row["ORF WC Score"],
        orfStatus:     row["ORF WC Status"],
        orfAccuracy:   row["ORF Accuracy Score"],
        nwfCLS:        row["NWF CLS Score"],
        nwfCLSStatus:  row["NWF CLS Status"],
        nwfWWR:        row["NWF WWR Score"],
        lnfScore:      row["LNF Score"],
        psf:           row["PSF Score"],
        psfStatus:     row["PSF Status"],
        fsf:           row["FSF Score"],
        mazeScore:     row["Maze Adjusted Score"],
        mazeStatus:    row["Maze Status"],
        retell:        row["Retell Score"],
        compositeScore: row["Reading Composite Score"],
        compositeStatus: row["Reading Composite Status"],
        lexile:        row["Lexile Reading"],
        ell:           row["ELL Status"],
        sped:          row["Special Education"],
        gender:        row["Gender"],
      });
    }
  }
  return { type:"acadience_reading", count: records.length, data: records };
}

function parseAcadienceMath(workbook) {
  const periods = ["BOY","MOY","EOY"];
  const records = [];

  for (const period of periods) {
    const sheetName = workbook.SheetNames.find(s =>
      s.includes("Acadience") && s.includes("Math") &&
      s.toUpperCase().includes(period)
    );
    if (!sheetName) continue;
    const ws = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(ws, { defval: null });

    for (const row of rows) {
      const code = row["Student"] || row["Student Code"];
      if (!code) continue;
      records.push({
        code,
        grade:           row["Student Grade Level"],
        period,
        compositeScore:  row["Math Composite Score"],
        compositeStatus: row["Math Composite Status"],
        compositeGoal:   row["Math Composite Goal"],
        ell:             row["ELL Status"],
        sped:            row["Special Education"],
        gender:          row["Gender"],
      });
    }
  }
  return { type:"acadience_math", count: records.length, data: records };
}

function parseObservations(workbook) {
  const sheetName = workbook.SheetNames.find(s =>
    s.includes("PageNot") || s.includes("MOTP")
  );
  if (!sheetName) return { type:"observations", count:0, data:[] };

  const ws = workbook.Sheets[sheetName];
  const allRows = XLSX.utils.sheet_to_json(ws, { header:1, defval: null });

  // Find header row (row with "TEACHER" in it)
  let headerRowIdx = -1;
  for (let i = 0; i < Math.min(15, allRows.length); i++) {
    if (allRows[i].some(c => c === "TEACHER")) {
      headerRowIdx = i;
      break;
    }
  }
  if (headerRowIdx === -1) return { type:"observations", count:0, data:[] };

  const headers = allRows[headerRowIdx];
  const records = [];

  for (let i = headerRowIdx + 1; i < allRows.length; i++) {
    const row = allRows[i];
    if (!row[2]) continue; // no teacher name
    const obj = {};
    headers.forEach((h, idx) => { if (h) obj[h] = row[idx]; });
    records.push({
      borough:     obj["NYCDOE Borough Office: "],
      dbn:         obj["DBN:"],
      teacher:     obj["TEACHER"],
      employeeId:  obj["Employee ID: "],
      obsOption:   obj["Observation Option: "],
      motpType:    obj["MOTP Type:                                             "],
      date:        obj["Observation Date: "],
      timePeriod:  obj["Time/ Period:"],
      observer:    obj["OBSERVER"],
      gradeLevel:  obj["Grade Level:"],
      subject:     obj["Subject:"],
      status:      obj["Status:"],
    });
  }
  return { type:"observations", count: records.length, data: records };
}

// ── DROP ZONE COMPONENT ───────────────────────────────────────
function DropZone({ source, onUpload, status }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const processFile = useCallback(async (file) => {
    onUpload(source.id, "processing", file.name);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type:"array", cellDates:true });
      const isDetected = source.detect(workbook.SheetNames);

      if (!isDetected) {
        onUpload(source.id, "error", `Could not detect expected sheets. Found: ${workbook.SheetNames.join(", ")}`);
        return;
      }

      const result = source.parse(workbook);
      onUpload(source.id, "success", file.name, result);
    } catch(e) {
      onUpload(source.id, "error", `Error reading file: ${e.message}`);
    }
  }, [source, onUpload]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) processFile(file);
  }, [processFile]);

  const bg = dragging ? source.color+"18"
    : status?.state === "success" ? T.mint
    : status?.state === "error" ? T.blush
    : T.white;

  const border = dragging ? `2px dashed ${source.color}`
    : status?.state === "success" ? `2px solid ${T.green}`
    : status?.state === "error" ? `2px solid ${T.rose}`
    : status?.state === "processing" ? `2px dashed ${source.color}`
    : `2px dashed ${T.mist}`;

  return (
    <div
      onDragOver={e=>{e.preventDefault();setDragging(true);}}
      onDragLeave={()=>setDragging(false)}
      onDrop={handleDrop}
      onClick={()=>!status?.state && inputRef.current?.click()}
      style={{
        background:bg, border, borderRadius:12, padding:"20px",
        cursor: status?.state ? "default" : "pointer",
        transition:"all .2s",
      }}
    >
      <input ref={inputRef} type="file" accept={source.accepts}
        style={{display:"none"}} onChange={handleChange}/>

      {/* Header */}
      <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:12}}>
        <div style={{
          width:40, height:40, borderRadius:10,
          background:source.color+"18", border:`1px solid ${source.color}33`,
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:20, flexShrink:0,
        }}>{source.icon}</div>
        <div>
          <div style={{fontSize:14, fontWeight:800, color:T.navy}}>{source.label}</div>
          <div style={{fontSize:11, color:T.slate}}>{source.description}</div>
        </div>
      </div>

      {/* Status / Drop area */}
      {!status || status.state === "idle" ? (
        <div style={{
          textAlign:"center", padding:"16px",
          background:T.bg, borderRadius:8, border:`1px dashed ${T.mist}`,
        }}>
          <div style={{fontSize:24, marginBottom:6}}>📂</div>
          <div style={{fontSize:12, fontWeight:700, color:T.slate}}>Drop file here or click to browse</div>
          <div style={{fontSize:11, color:T.slate, marginTop:3}}>{source.accepts.split(",").join(" · ")}</div>
        </div>
      ) : status.state === "processing" ? (
        <div style={{textAlign:"center", padding:"16px", background:source.color+"08", borderRadius:8}}>
          <div style={{fontSize:20, marginBottom:4}}>⏳</div>
          <div style={{fontSize:12, fontWeight:700, color:source.color}}>Processing {status.filename}...</div>
        </div>
      ) : status.state === "success" ? (
        <div>
          <div style={{
            display:"flex", alignItems:"center", gap:8,
            background:T.mint, borderRadius:8, padding:"10px 14px", marginBottom:10,
          }}>
            <span style={{fontSize:18}}>✅</span>
            <div>
              <div style={{fontSize:12, fontWeight:800, color:T.green}}>
                {status.result?.count} records loaded from {status.filename}
              </div>
              <div style={{fontSize:11, color:T.slate}}>Ready to use in dashboard</div>
            </div>
            <button onClick={(e)=>{e.stopPropagation();onUpload(source.id,"idle");}}
              style={{marginLeft:"auto", border:`1px solid ${T.mist}`, background:T.white,
                borderRadius:6, padding:"3px 10px", cursor:"pointer", fontSize:11,
                color:T.slate, fontFamily:"inherit"}}>
              Replace
            </button>
          </div>
          {/* Preview */}
          {status.result?.data?.length > 0 && (
            <DataPreview data={status.result.data} type={status.result.type} color={source.color}/>
          )}
        </div>
      ) : status.state === "error" ? (
        <div style={{
          background:T.blush, borderRadius:8, padding:"12px 14px",
          display:"flex", gap:8, alignItems:"flex-start",
        }}>
          <span style={{fontSize:16}}>❌</span>
          <div>
            <div style={{fontSize:12, fontWeight:800, color:T.rose}}>Upload failed</div>
            <div style={{fontSize:11, color:T.slate, marginTop:2}}>{status.filename}</div>
          </div>
          <button onClick={(e)=>{e.stopPropagation();onUpload(source.id,"idle");}}
            style={{marginLeft:"auto", border:`1px solid ${T.rose}44`, background:T.white,
              borderRadius:6, padding:"3px 10px", cursor:"pointer", fontSize:11,
              color:T.rose, fontFamily:"inherit"}}>
            Retry
          </button>
        </div>
      ) : null}

      {/* Format guide */}
      {(!status || status.state === "idle") && (
        <details style={{marginTop:10}}>
          <summary style={{fontSize:11, color:T.slate, cursor:"pointer", fontWeight:600}}>
            📋 Expected format
          </summary>
          <pre style={{
            fontSize:10, color:T.slate, background:T.bg,
            borderRadius:6, padding:"8px", marginTop:6,
            whiteSpace:"pre-wrap", lineHeight:1.6,
          }}>{source.format}</pre>
        </details>
      )}
    </div>
  );
}

// ── DATA PREVIEW TABLE ────────────────────────────────────────
function DataPreview({ data, type, color }) {
  const [expanded, setExpanded] = useState(false);
  const preview = data.slice(0, expanded ? 10 : 3);

  const cols = {
    roster:           ["code","grade","teacherAbbr","classId"],
    iready_ela:       ["code","grade","elaScore","elaLevel","percentile","lexile"],
    iready_math:      ["code","grade","mathScore","mathLevel","percentile","quantile"],
    acadience_reading:["code","grade","period","orfScore","orfStatus","compositeScore","compositeStatus"],
    acadience_math:   ["code","grade","period","compositeScore","compositeStatus"],
    observations:     ["teacher","date","gradeLevel","subject","observer","status"],
  }[type] || Object.keys(data[0]).slice(0,5);

  const labels = {
    code:"Student Code", grade:"Grade", teacherAbbr:"Teacher",
    classId:"Class", elaScore:"ELA Score", elaLevel:"ELA Level",
    mathScore:"Math Score", mathLevel:"Math Level",
    percentile:"Percentile", lexile:"Lexile", quantile:"Quantile",
    period:"Period", orfScore:"ORF", orfStatus:"ORF Status",
    compositeScore:"Composite", compositeStatus:"Status",
    teacher:"Teacher", date:"Date", gradeLevel:"Grade",
    subject:"Subject", observer:"Observer", status:"Status",
  };

  return (
    <div style={{marginTop:10}}>
      <div style={{fontSize:11, fontWeight:700, color:T.slate, marginBottom:6}}>
        Preview ({data.length} total records):
      </div>
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%", borderCollapse:"collapse", fontSize:11}}>
          <thead>
            <tr style={{background:color+"12"}}>
              {cols.map(c=>(
                <th key={c} style={{padding:"5px 8px", textAlign:"left",
                  color:color, fontWeight:700, borderBottom:`1px solid ${color}33`,
                  whiteSpace:"nowrap"}}>
                  {labels[c]||c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preview.map((row,i)=>(
              <tr key={i} style={{background:i%2===0?T.white:T.bg}}>
                {cols.map(c=>(
                  <td key={c} style={{padding:"4px 8px", color:T.navy,
                    borderBottom:`1px solid ${T.mist}`, whiteSpace:"nowrap"}}>
                    {row[c] instanceof Date
                      ? row[c].toLocaleDateString()
                      : String(row[c]??"—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.length > 3 && (
        <button onClick={()=>setExpanded(!expanded)} style={{
          marginTop:6, fontSize:11, color:color, background:"none",
          border:"none", cursor:"pointer", fontWeight:600, fontFamily:"inherit",
        }}>
          {expanded ? "▲ Show less" : `▼ Show ${Math.min(10, data.length-3)} more`}
        </button>
      )}
    </div>
  );
}

// ── MULTI-FILE UPLOAD (upload the whole workbook at once) ─────
function MultiFileUpload({ onAllParsed }) {
  const [dragging, setDragging] = useState(false);
  const [status,   setStatus]   = useState(null);
  const inputRef = useRef(null);

  const processFile = useCallback(async (file) => {
    setStatus({state:"processing", filename:file.name});
    try {
      const buffer  = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, {type:"array", cellDates:true});
      const detected = [];

      for (const source of DATA_SOURCES) {
        if (source.detect(workbook.SheetNames)) {
          const result = source.parse(workbook);
          detected.push({ source, result });
        }
      }

      if (detected.length === 0) {
        setStatus({state:"error", filename:file.name,
          msg:`No recognized data found. Sheets in file: ${workbook.SheetNames.join(", ")}`});
        return;
      }

      setStatus({state:"success", filename:file.name, detected});
      onAllParsed(detected);
    } catch(e) {
      setStatus({state:"error", filename:file.name, msg:e.message});
    }
  }, [onAllParsed]);

  const handleDrop = useCallback((e)=>{
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  },[processFile]);

  return (
    <div style={{
      border:`2px dashed ${dragging ? T.blue : T.mist}`,
      borderRadius:14, padding:"28px", textAlign:"center",
      background: dragging ? T.blue+"08" : T.white,
      transition:"all .2s", cursor:"pointer",
    }}
      onDragOver={e=>{e.preventDefault();setDragging(true);}}
      onDragLeave={()=>setDragging(false)}
      onDrop={handleDrop}
      onClick={()=>inputRef.current?.click()}
    >
      <input ref={inputRef} type="file" accept=".xlsx,.xls"
        style={{display:"none"}} onChange={e=>e.target.files[0]&&processFile(e.target.files[0])}/>

      {!status || status.state==="idle" ? (
        <>
          <div style={{fontSize:40, marginBottom:10}}>📊</div>
          <div style={{fontSize:16, fontWeight:900, color:T.navy, marginBottom:6}}>
            Drop your master data workbook here
          </div>
          <div style={{fontSize:13, color:T.slate, marginBottom:8}}>
            Upload the full Excel file and we'll auto-detect all data types inside it
          </div>
          <div style={{fontSize:11, color:T.slate}}>
            Works with: iReady exports · Acadience exports · ROCL roster files
          </div>
        </>
      ) : status.state==="processing" ? (
        <>
          <div style={{fontSize:32, marginBottom:8}}>⏳</div>
          <div style={{fontSize:14, fontWeight:700, color:T.blue}}>Reading {status.filename}...</div>
        </>
      ) : status.state==="success" ? (
        <>
          <div style={{fontSize:32, marginBottom:8}}>✅</div>
          <div style={{fontSize:14, fontWeight:800, color:T.green, marginBottom:6}}>
            {status.filename} — {status.detected.length} data sources detected!
          </div>
          <div style={{display:"flex", justifyContent:"center", gap:8, flexWrap:"wrap"}}>
            {status.detected.map(({source,result})=>(
              <span key={source.id} style={{
                display:"inline-flex", alignItems:"center", gap:4,
                background:source.color+"15", border:`1px solid ${source.color}44`,
                color:source.color, borderRadius:99, padding:"3px 12px",
                fontSize:12, fontWeight:700,
              }}>
                {source.icon} {source.label} ({result.count} records)
              </span>
            ))}
          </div>
          <button onClick={e=>{e.stopPropagation();setStatus(null);}}
            style={{marginTop:12, border:`1px solid ${T.mist}`, background:T.bg,
              borderRadius:7, padding:"6px 16px", cursor:"pointer", fontSize:12,
              color:T.slate, fontFamily:"inherit"}}>
            Upload different file
          </button>
        </>
      ) : (
        <>
          <div style={{fontSize:32, marginBottom:8}}>❌</div>
          <div style={{fontSize:13, fontWeight:700, color:T.rose}}>{status.msg}</div>
          <button onClick={e=>{e.stopPropagation();setStatus(null);}}
            style={{marginTop:10, border:`1px solid ${T.rose}44`, background:T.white,
              borderRadius:7, padding:"5px 14px", cursor:"pointer", fontSize:12,
              color:T.rose, fontFamily:"inherit"}}>
            Try again
          </button>
        </>
      )}
    </div>
  );
}

// ── MAIN EXPORT ───────────────────────────────────────────────
export default function DataUploadCenter({ onDataLoaded }) {
  const [uploadStatus, setUploadStatus] = useState({});
  const [activeTab,    setActiveTab]    = useState("auto");
  const [loadedData,   setLoadedData]   = useState({});

  const handleUpload = useCallback((sourceId, state, filename, result) => {
    setUploadStatus(prev => ({
      ...prev,
      [sourceId]: { state, filename, result },
    }));
    if (state === "success" && result) {
      setLoadedData(prev => ({ ...prev, [sourceId]: result }));
      if (onDataLoaded) onDataLoaded({ ...loadedData, [sourceId]: result });
    }
    if (state === "idle") {
      setUploadStatus(prev => { const n={...prev}; delete n[sourceId]; return n; });
    }
  }, [loadedData, onDataLoaded]);

  const handleAllParsed = useCallback((detected) => {
    const newData = { ...loadedData };
    const newStatus = { ...uploadStatus };
    for (const { source, result } of detected) {
      newData[source.id] = result;
      newStatus[source.id] = { state:"success", filename:"multi-upload", result };
    }
    setLoadedData(newData);
    setUploadStatus(newStatus);
    if (onDataLoaded) onDataLoaded(newData);
  }, [loadedData, uploadStatus, onDataLoaded]);

  const successCount = Object.values(uploadStatus).filter(s=>s.state==="success").length;
  const totalRecords = Object.values(loadedData).reduce((a,d)=>a+(d?.count||0),0);

  return (
    <div style={{fontFamily:"'DM Sans','Segoe UI',sans-serif", display:"flex", flexDirection:"column", gap:20}}>

      {/* Header */}
      <div>
        <div style={{display:"flex", alignItems:"center", gap:10}}>
          <span style={{fontSize:24}}>📤</span>
          <span style={{fontSize:20, fontWeight:900, color:T.navy}}>Data Upload Center</span>
          {successCount > 0 && (
            <span style={{
              background:T.green+"20", border:`1px solid ${T.green}44`,
              color:T.green, borderRadius:99, padding:"2px 12px",
              fontSize:12, fontWeight:700,
            }}>
              {successCount} source{successCount>1?"s":""} loaded · {totalRecords.toLocaleString()} records
            </span>
          )}
        </div>
        <div style={{fontSize:12, color:T.slate, marginTop:4, marginLeft:34}}>
          Admin only · Upload real school data to populate the dashboard · Files processed locally — nothing is sent to external servers
        </div>
      </div>

      {/* FERPA notice */}
      <div style={{
        background:"#FEF3C7", border:"1px solid #D97706", borderRadius:10,
        padding:"10px 16px", display:"flex", gap:10, alignItems:"flex-start",
      }}>
        <span style={{fontSize:16, flexShrink:0}}>🔒</span>
        <div style={{fontSize:12, color:"#92400E"}}>
          <strong>FERPA Notice:</strong> Student data is processed in your browser only.
          Data is not uploaded to any server or stored permanently.
          Each session starts fresh — re-upload files when you return.
          For persistent storage, contact your data coordinator about a backend setup.
        </div>
      </div>

      {/* Tab selector */}
      <div style={{display:"flex", gap:8}}>
        {[
          {id:"auto",     label:"🚀 Auto-Detect (Recommended)", desc:"Drop one file, we detect everything"},
          {id:"manual",   label:"📁 Upload by Type",            desc:"Upload each data source separately"},
        ].map(tab=>(
          <button key={tab.id} onClick={()=>setActiveTab(tab.id)} style={{
            padding:"10px 20px", borderRadius:10, border:"none", cursor:"pointer",
            fontFamily:"inherit", textAlign:"left",
            background: activeTab===tab.id ? T.blue : T.white,
            color:       activeTab===tab.id ? "white" : T.slate,
            boxShadow:   activeTab===tab.id ? `0 2px 8px ${T.blue}33` : "none",
            border: activeTab!==tab.id ? `1px solid ${T.mist}` : "none",
          }}>
            <div style={{fontSize:13, fontWeight:800}}>{tab.label}</div>
            <div style={{fontSize:11, opacity:.8, marginTop:2}}>{tab.desc}</div>
          </button>
        ))}
      </div>

      {/* Auto-detect tab */}
      {activeTab === "auto" && (
        <div style={{display:"flex", flexDirection:"column", gap:16}}>
          <MultiFileUpload onAllParsed={handleAllParsed}/>

          {/* Show loaded sources summary */}
          {successCount > 0 && (
            <div style={{
              background:T.white, borderRadius:14, border:`1px solid ${T.mist}`,
              padding:"18px 20px",
            }}>
              <div style={{fontSize:13, fontWeight:800, color:T.navy, marginBottom:12}}>
                ✅ Loaded Data Sources
              </div>
              <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:10}}>
                {DATA_SOURCES.filter(s=>loadedData[s.id]).map(source=>(
                  <div key={source.id} style={{
                    background:source.color+"10", border:`1px solid ${source.color}33`,
                    borderRadius:10, padding:"10px 14px",
                  }}>
                    <div style={{fontSize:18, marginBottom:4}}>{source.icon}</div>
                    <div style={{fontSize:12, fontWeight:800, color:T.navy}}>{source.label}</div>
                    <div style={{fontSize:11, color:source.color, fontWeight:700}}>
                      {loadedData[source.id].count} records
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Manual upload tab */}
      {activeTab === "manual" && (
        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:16}}>
          {DATA_SOURCES.map(source=>(
            <DropZone
              key={source.id}
              source={source}
              status={uploadStatus[source.id]}
              onUpload={handleUpload}
            />
          ))}
        </div>
      )}

      {/* Apply to dashboard button */}
      {successCount > 0 && (
        <div style={{
          background:T.navy, borderRadius:14, padding:"18px 24px",
          display:"flex", alignItems:"center", justifyContent:"space-between",
        }}>
          <div>
            <div style={{fontSize:14, fontWeight:900, color:"white"}}>
              {totalRecords.toLocaleString()} records ready to load
            </div>
            <div style={{fontSize:12, color:"rgba(255,255,255,.7)", marginTop:2}}>
              {successCount} data source{successCount>1?"s":""} successfully parsed
            </div>
          </div>
          <button
            onClick={()=>onDataLoaded && onDataLoaded(loadedData)}
            style={{
              background:T.teal, color:"white", border:"none",
              borderRadius:10, padding:"12px 28px", cursor:"pointer",
              fontSize:14, fontWeight:800, fontFamily:"inherit",
              boxShadow:`0 4px 16px ${T.teal}44`,
            }}>
            Apply to Dashboard →
          </button>
        </div>
      )}
    </div>
  );
}
